import React, {useEffect, useMemo, useState} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
//local
import {JOINING_ORG, toOrganizationId} from "constants/routes.const";
import {Header} from "../Header/Header";
import {Footer} from "../Footer";
import {SideBar} from "../SideBar";
import {InfoStrip} from "components/InfoStrip";
import CustomCollapse from "components/CustomCollapse";
import {CustomBadge} from "components/CustomBadge";
import ButtonLink from "components/buttons/ButtonLink";
import {ModalOwnerInvite} from "components/Settings/sections/components/ModalOwnerInvite";
import {identifyModalNames} from "constants/modal.conts";
import {showSuccess} from "helpers/notifications.helper";
import {ModalAbout} from "components/modals/ModalAbout";
import {ModalAdvertisementMain} from "components/modals/ModalAdvertisementMain";
import {ModalEmptyInsuranceAttention} from "components/modals/ModalEmptyInsuranceAttention";
import {getStorage, setStorage} from "helpers/storage";
import {getFullName, hasPermission} from "helpers/helper";
import {oneDay, fourDays} from "constants/user.consts";
import {color} from "constants/color.consts";

// messages longer than this get a "show more / show less" toggle
const JOIN_MESSAGE_PREVIEW_LENGTH = 80;

export const Layout = inject("store")(
  observer(
    ({
      store: {auth, organization, buyer, seller, clientStore, publicStore},
      children,
    }) => {
      const navigate = useNavigate();
      const {user, token, activeOrganizationId, organizationName, isStaff} = auth;
      const {isBankId, isOrgSettings, isOnboardingVideo, memberInfo} = organization;
      const {commonSettings} = publicStore;
      const {t} = useTranslation();
      const location = useLocation();
      const lastNoInsuranceModalDate =
        user.ActiveOrganization?.userLastNoInsuranceNotificationDate;
      const [isAdvertisementVideo, setAdvertisementVideo] = useState(false);
      const [joinRequestUserId, setJoinRequestUserId] = useState(null);
      const [isJoinRequestsOpen, setJoinRequestsOpen] = useState(false);
      const [expandedJoinMessages, setExpandedJoinMessages] = useState({});
      const [isEmptyInsuranceAttentionModal, setEmptyInsuranceAttentionModal] = useState(
        lastNoInsuranceModalDate
          ? Date.now() - Date.parse(lastNoInsuranceModalDate) > fourDays
          : lastNoInsuranceModalDate
      );

      const isConfirmed = useMemo(() => user.isConfirmed, [user]);
      const advertisementMainData = useMemo(
        () => user?.advertisements?.filter((item) => item.place === "main"),
        [user]
      );

      const showOnboardingVideo = useMemo(
        () => isBankId && isOrgSettings && !isOnboardingVideo,
        [isBankId, isOnboardingVideo, isOrgSettings]
      );

      const showAdvertisementMainModal = useMemo(
        () =>
          isBankId &&
          isOrgSettings &&
          isOnboardingVideo &&
          advertisementMainData &&
          !!advertisementMainData?.length &&
          isAdvertisementVideo,
        [
          advertisementMainData,
          isAdvertisementVideo,
          isBankId,
          isOnboardingVideo,
          isOrgSettings,
        ]
      );

      const isJoiningOrgPage = location.pathname.includes(JOINING_ORG);
      const showFooter = !isJoiningOrgPage;
      // a user waiting to join has no pages to go to, every link leads back to the waiting page
      const showSideBar = !isJoiningOrgPage;
      const showInfoStrip = commonSettings?.infoStrip?.active && !isJoiningOrgPage;
      const showAttentionStrip = token && !isConfirmed && !isJoiningOrgPage;
      const showJoinRequestStrips =
        token && isStaff && !isJoiningOrgPage && !!memberInfo?.length;
      const isJoinRequestsCollapsible = memberInfo?.length > 1;

      const checkAdvertisementMainDate = () => {
        const advertisementMain = getStorage("advertisementMain");
        if (advertisementMain) {
          if (Date.now() - advertisementMain > oneDay) {
            setAdvertisementVideo(true);
            setStorage("advertisementMain", Date.now());
          }
        } else {
          setAdvertisementVideo(true);
          setStorage("advertisementMain", Date.now());
        }
      };

      const sendEmailAndOpenModal = () => {
        auth.sendConfirmationEmail();
        showSuccess(t("SUCCESSFULLY_SENT_CODE"));
        auth.updateAuthModalName(identifyModalNames.identifyEmailAddress);
      };

      const handleAcceptJoinRequest = () => {
        const requestedUserId = joinRequestUserId;
        setJoinRequestUserId(null);
        organization.joinRequestAccept(requestedUserId).then(() => {
          organization.requestsToJoinOrganization();
          organization.getOrganizationMembers();
        });
      };

      const toggleJoinMessage = (userId) =>
        setExpandedJoinMessages((prev) => ({...prev, [userId]: !prev[userId]}));

      const handleDenyJoinRequest = (requestedUserId) =>
        organization
          .joinRequestDeny(requestedUserId)
          .then(() => organization.requestsToJoinOrganization());

      const renderJoinRequestStrip = ({User, joinMessage}) => (
        <InfoStrip
          key={User.id}
          color={color.orange}
          text={
            <span className="info-strip__text">
              <strong>
                {getFullName(User.firstName, User.lastName)
                  ? `${getFullName(User.firstName, User.lastName)} (${User.email})`
                  : User.email}{" "}
                {t("IS_REQUESTING_MEMBERSHIP")} {user?.ActiveOrganization?.name}
              </strong>
              {joinMessage && (
                <span
                  className={`info-strip__message ${
                    expandedJoinMessages[User.id] ? "__expanded" : ""
                  }`}
                >
                  {`"${joinMessage}"`}
                </span>
              )}
              {joinMessage?.length > JOIN_MESSAGE_PREVIEW_LENGTH && (
                <button
                  type="button"
                  className="info-strip__toggle"
                  onClick={() => toggleJoinMessage(User.id)}
                >
                  {expandedJoinMessages[User.id]
                    ? t("SHOW_LESS", "Visa mindre")
                    : t("SHOW_MORE", "Visa mer")}
                </button>
              )}
            </span>
          }
        >
          <ButtonLink
            text={t("ACCEPT_MEMBERSHIP")}
            addClass="info-strip__btn __filled"
            onClick={() => setJoinRequestUserId(User.id)}
          />
          <ButtonLink
            text={t("DENY_MEMBERSHIP")}
            addClass="info-strip__btn"
            onClick={() => handleDenyJoinRequest(User.id)}
          />
        </InfoStrip>
      );

      const onClickOnboardingVideo = () => {
        organization
          .setOrganizationOnboardingStatus(activeOrganizationId, {
            onboardingVideo: true,
          })
          .then(() => organization.setOnboardingVideoStatus(true))
          .finally(() => showSuccess(t("ONBOARDING_MODAL_ABOUT_THANKS_TEXT")));
      };

      const setLastNoInsuranceNotificationDate = (isNavigate) =>
        auth
          .setLastNoInsuranceNotificationDate({
            lastNoInsuranceNotificationDate: new Date().toISOString(),
          })
          .then(() => {
            auth.setUserLastNoInsuranceNotificationDate(new Date().toISOString());
            isNavigate &&
              navigate(toOrganizationId(activeOrganizationId), {
                state: {editState: {insurance: true}},
              });
          })
          .finally(() => setEmptyInsuranceAttentionModal(false));

      useEffect(() => {}, [organization.blacklistOrganization]);

      useEffect(() => {
        // without an organization there is nobody to let in
        token &&
          activeOrganizationId &&
          isStaff &&
          organization.requestsToJoinOrganization();
      }, [organization, token, isStaff, activeOrganizationId]);

      useEffect(() => {
        advertisementMainData &&
          advertisementMainData?.length &&
          checkAdvertisementMainDate();
      }, [advertisementMainData]);

      return (
        <div className="main-content_wrapper">
          {showInfoStrip && (
            <InfoStrip color={color.green} text={commonSettings?.infoStrip?.text} />
          )}
          {showAttentionStrip && (
            <InfoStrip
              onClick={sendEmailAndOpenModal}
              color={color.orange}
              text={t("VERIFY_EMAIL_TEXT")}
            />
          )}
          {showJoinRequestStrips &&
            !isJoinRequestsCollapsible &&
            renderJoinRequestStrip(memberInfo[0])}
          {showJoinRequestStrips && isJoinRequestsCollapsible && (
            <InfoStrip color={color.orange}>
              <CustomCollapse
                title={t("PENDING_MEMBERSHIP_REQUESTS_TITLE").format(organizationName)}
                titleSuffixContent={
                  <CustomBadge count={memberInfo.length} addClass="mr-2" />
                }
                noTranslate
                bold
                addClass="info-strip__collapse"
                isActive={isJoinRequestsOpen}
                onChange={() => setJoinRequestsOpen((prev) => !prev)}
              />
            </InfoStrip>
          )}
          {showJoinRequestStrips && isJoinRequestsCollapsible && isJoinRequestsOpen && (
            <div className="info-strip__list">
              {memberInfo.map(renderJoinRequestStrip)}
            </div>
          )}
          <Header type={isJoiningOrgPage ? "joining-org" : "profile"} />
          <div className="layout-content_wrapper">
            {showSideBar && (
              <>
                <SideBar
                  auth={auth}
                  buyer={buyer}
                  seller={seller}
                  organization={organization}
                  clientStore={clientStore}
                  showSidebar={clientStore.showSidebar ? "showSidebar" : "disabled"}
                />
                <span
                  className={`abs-bg_sidebar ${
                    clientStore.showSidebar ? "showSidebar" : "disabled"
                  }`}
                  onClick={() => clientStore.setShowSidebar(false)}
                />
              </>
            )}
            <div className="layout-content_inner-wrap">{children}</div>
          </div>

          {showOnboardingVideo && <ModalAbout onClick={onClickOnboardingVideo} />}

          {showAdvertisementMainModal && !hasPermission(["HIDE_MAIN_ADVERTISEMENT"]) && (
            <ModalAdvertisementMain
              links={advertisementMainData}
              timeout={1000}
              type={advertisementMainData[0].type}
              onClose={() => setAdvertisementVideo(false)}
            />
          )}

          {isEmptyInsuranceAttentionModal && isStaff && (
            <ModalEmptyInsuranceAttention
              onOk={() => setLastNoInsuranceNotificationDate(true)}
              onClose={() => setLastNoInsuranceNotificationDate()}
            />
          )}
          {joinRequestUserId && (
            <ModalOwnerInvite
              users={["user"]}
              onAccept={handleAcceptJoinRequest}
              onClose={() => setJoinRequestUserId(null)}
            />
          )}
          {showFooter && <Footer />}
        </div>
      );
    }
  )
);
